/**
 * Organization Branding Settings
 * Allows admins to customize their tenant's visual branding
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../../config/api';
import { 
  Palette, 
  Upload, 
  Image as ImageIcon, 
  Eye, 
  Check,
  Loader2,
  RefreshCw
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
const API = API_BASE;

// Predefined color palette for quick selection
const COLOR_PRESETS = [
  { name: 'Cerebro Gold', color: '#D4AF37' },
  { name: 'Corporate Blue', color: '#1e40af' },
  { name: 'Forest Green', color: '#166534' },
  { name: 'Royal Purple', color: '#7c3aed' },
  { name: 'Sunset Orange', color: '#ea580c' },
  { name: 'Slate Gray', color: '#475569' },
  { name: 'Rose Red', color: '#e11d48' },
  { name: 'Teal', color: '#0d9488' },
];

export default function BrandingSettings({ token, organizationId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  const [branding, setBranding] = useState({
    name: '',
    logo_url: null,
    primary_color: '#D4AF37',
    favicon_url: null,
    login_background_url: null
  });
  
  const [previewColor, setPreviewColor] = useState('#D4AF37');

  useEffect(() => {
    fetchBranding();
  }, [organizationId]);

  const fetchBranding = async () => {
    if (!organizationId) {
      setLoading(false);
      return;
    }
    
    try {
      const res = await axios.get(`${API}/api/organizations/${organizationId}/branding`);
      setBranding(res.data);
      setPreviewColor(res.data.primary_color || '#D4AF37');
    } catch (error) {
      console.error('Failed to fetch branding:', error);
      toast.error('Failed to load branding settings');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file
    if (!['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'].includes(file.type)) {
      toast.error('Invalid file type. Please upload PNG, JPEG, WebP, or SVG');
      return;
    }
    
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 2MB');
      return;
    }
    
    setUploadingLogo(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(
        `${API}/api/organizations/${organizationId}/branding/logo`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setBranding(prev => ({ ...prev, logo_url: res.data.logo_url }));
      toast.success('Logo uploaded successfully');
    } catch (error) {
      console.error('Failed to upload logo:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSaveBranding = async () => {
    setSaving(true);
    
    try {
      await axios.put(
        `${API}/api/organizations/${organizationId}/branding`,
        { primary_color: previewColor },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setBranding(prev => ({ ...prev, primary_color: previewColor }));
      toast.success('Branding saved successfully');
      
      // Apply to current page
      document.documentElement.style.setProperty('--tenant-primary', previewColor);
    } catch (error) {
      console.error('Failed to save branding:', error);
      toast.error(error.response?.data?.detail || 'Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-navigate-gold" />
      </div>
    );
  }

  if (!organizationId) {
    return (
      <div className="p-6 bg-navigate-surface border border-zinc-800 rounded-xl">
        <p className="text-zinc-400 text-center">
          Please create or select an organization to configure branding.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-xl font-bold text-white flex items-center gap-2">
            <Palette className="h-5 w-5 text-navigate-gold" />
            Branding Settings
          </h2>
          <p className="text-sm text-zinc-400 mt-1">
            Customize your organization's visual appearance
          </p>
        </div>
        <Button
          onClick={handleSaveBranding}
          disabled={saving || previewColor === branding.primary_color}
          className="bg-navigate-gold text-black hover:bg-navigate-gold-light"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Check className="h-4 w-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left Column - Settings */}
        <div className="space-y-6">
          {/* Logo Upload */}
          <div className="p-6 bg-navigate-surface border border-zinc-800 rounded-xl">
            <Label className="text-white mb-4 block">Organization Logo</Label>
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-lg bg-zinc-800 border border-zinc-700 flex items-center justify-center overflow-hidden">
                {branding.logo_url ? (
                  <img 
                    src={branding.logo_url} 
                    alt="Logo" 
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <ImageIcon className="h-8 w-8 text-zinc-600" />
                )}
              </div>
              <div className="flex-1">
                <label className="cursor-pointer">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors">
                    {uploadingLogo ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                  </div>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    onChange={handleLogoUpload}
                    disabled={uploadingLogo}
                    className="hidden"
                  />
                </label>
                <p className="text-xs text-zinc-500 mt-2">
                  PNG, JPEG, WebP or SVG. Max 2MB.
                </p>
              </div>
            </div>
          </div>

          {/* Primary Color */}
          <div className="p-6 bg-navigate-surface border border-zinc-800 rounded-xl">
            <Label className="text-white mb-4 block">Primary Accent Color</Label>
            
            {/* Color Presets */}
            <div className="flex flex-wrap gap-2 mb-4">
              {COLOR_PRESETS.map((preset) => (
                <button
                  key={preset.color}
                  onClick={() => setPreviewColor(preset.color)}
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    previewColor === preset.color 
                      ? 'border-white scale-110' 
                      : 'border-transparent hover:scale-105'
                  }`}
                  style={{ backgroundColor: preset.color }}
                  title={preset.name}
                />
              ))}
            </div>
            
            {/* Custom Color Input */}
            <div className="flex items-center gap-3">
              <div 
                className="w-12 h-12 rounded-lg border border-zinc-700"
                style={{ backgroundColor: previewColor }}
              />
              <Input
                type="text"
                value={previewColor}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^#[0-9A-Fa-f]{0,6}$/.test(value)) {
                    setPreviewColor(value);
                  }
                }}
                placeholder="#D4AF37"
                className="w-32 bg-zinc-800 border-zinc-700 font-mono"
              />
              <input
                type="color"
                value={previewColor}
                onChange={(e) => setPreviewColor(e.target.value)}
                className="w-10 h-10 rounded cursor-pointer bg-transparent border-0"
              />
            </div>
          </div>

          {/* Powered by Notice */}
          <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            <p className="text-sm text-amber-400">
              <strong>Note:</strong> The "Powered by Cerebro Core" badge will always be visible 
              on your tenant's login page and footer. This cannot be disabled.
            </p>
          </div>
        </div>

        {/* Right Column - Preview */}
        <div className="p-6 bg-navigate-surface border border-zinc-800 rounded-xl">
          <div className="flex items-center gap-2 mb-4">
            <Eye className="h-5 w-5 text-zinc-400" />
            <Label className="text-white">Live Preview</Label>
          </div>
          
          {/* Mini Login Preview */}
          <div className="bg-navigate-dark rounded-lg p-6 border border-zinc-700">
            <div className="flex items-center justify-center gap-2 mb-6">
              {branding.logo_url ? (
                <img src={branding.logo_url} alt="Logo" className="h-8 w-8" />
              ) : (
                <div className="h-8 w-8 rounded bg-zinc-700" />
              )}
              <span 
                className="font-heading font-bold"
                style={{ color: previewColor }}
              >
                {branding.name || 'Your Organization'}
              </span>
            </div>
            
            <div className="space-y-3">
              <div className="h-10 bg-zinc-800 rounded border border-zinc-700" />
              <div className="h-10 bg-zinc-800 rounded border border-zinc-700" />
              <button
                className="w-full h-10 rounded font-medium text-black"
                style={{ backgroundColor: previewColor }}
              >
                Sign In
              </button>
            </div>
            
            <div className="mt-4 pt-4 border-t border-zinc-700 flex items-center justify-center gap-1.5 text-xs text-zinc-500">
              <span>Powered by</span>
              <span className="font-semibold text-navigate-gold">Cerebro Core</span>
            </div>
          </div>
          
          {/* Button Preview */}
          <div className="mt-4 space-y-2">
            <p className="text-xs text-zinc-500">Button & Link Previews:</p>
            <div className="flex items-center gap-3">
              <button
                className="px-4 py-2 rounded-lg font-medium text-black"
                style={{ backgroundColor: previewColor }}
              >
                Primary Button
              </button>
              <span 
                className="cursor-pointer hover:underline"
                style={{ color: previewColor }}
              >
                Link Text
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
